export const statusConfig: Record<
  string,
  { colorPalette: string; state: string; label: string }
> = {
  pending: { colorPalette: 'gray', state: 'pending', label: 'Pending' },
  queued: { colorPalette: 'gray', state: 'pending', label: 'Queued' },
  running: { colorPalette: 'blue', state: 'active', label: 'Running' },
  completed: { colorPalette: 'green', state: 'success', label: 'Completed' },
  failed: { colorPalette: 'red', state: 'error', label: 'Failed' },
  stopped: { colorPalette: 'gray', state: 'default', label: 'Stopped' },
  merged: { colorPalette: 'purple', state: 'success', label: 'Merged' },
  cancelled: { colorPalette: 'gray', state: 'default', label: 'Cancelled' },
  waiting_for_input: {
    colorPalette: 'yellow',
    state: 'warning',
    label: 'Waiting for input',
  },
  waiting: {
    colorPalette: 'yellow',
    state: 'warning',
    label: 'Waiting',
  },
  paused: { colorPalette: 'yellow', state: 'warning', label: 'Paused' },
  resuming: { colorPalette: 'blue', state: 'active', label: 'Resuming' },
  needs_attention: {
    colorPalette: 'orange',
    state: 'warning',
    label: 'Needs attention',
  },
  ready_for_review: {
    colorPalette: 'green',
    state: 'success',
    label: 'Ready for review',
  },
  cancelling: {
    colorPalette: 'gray',
    state: 'active',
    label: 'Cancelling',
  },
  preparing_workspace: {
    colorPalette: 'blue',
    state: 'active',
    label: 'Preparing workspace',
  },
  working: { colorPalette: 'blue', state: 'active', label: 'Working' },
  pausing: { colorPalette: 'yellow', state: 'active', label: 'Pausing' },
  needs_input: {
    colorPalette: 'yellow',
    state: 'warning',
    label: 'Needs input',
  },
  awaiting_approval: {
    colorPalette: 'orange',
    state: 'warning',
    label: 'Awaiting approval',
  },
  rate_limited: {
    colorPalette: 'orange',
    state: 'warning',
    label: 'Rate limited',
  },
  waiting_for_provider: {
    colorPalette: 'orange',
    state: 'warning',
    label: 'Provider unavailable',
  },
  waiting_for_ci: {
    colorPalette: 'blue',
    state: 'pending',
    label: 'Waiting for CI',
  },
  waiting_for_external_event: {
    colorPalette: 'blue',
    state: 'pending',
    label: 'Waiting for external event',
  },
  waking_workspace: {
    colorPalette: 'blue',
    state: 'active',
    label: 'Waking workspace',
  },
  finalizing: {
    colorPalette: 'blue',
    state: 'active',
    label: 'Finalizing',
  },
}

export function isProgressingActivity(activityKey: string): boolean {
  return (
    activityKey === 'queued' ||
    activityKey === 'preparing_workspace' ||
    activityKey === 'working' ||
    activityKey === 'pausing' ||
    activityKey === 'resuming' ||
    activityKey === 'waking_workspace' ||
    activityKey === 'finalizing' ||
    activityKey === 'cancelling'
  )
}

export const SYSTEM_STEP_NAMES = [
  'setup_repo',
  'plan',
  'analyze',
  'implement',
  'implement_fix',
  'test',
  'verify',
  'open_pr',
  'await_ci',
  'ready_for_review',
  'process_feedback',
] as const

export type SystemStepName = (typeof SYSTEM_STEP_NAMES)[number]

const SYSTEM_STEP_NAME_SET = new Set<string>(SYSTEM_STEP_NAMES)

export function isSystemStepName(name: string): name is SystemStepName {
  return SYSTEM_STEP_NAME_SET.has(name)
}

export const stepLabels: Record<SystemStepName, string> = {
  setup_repo: 'Setting up sandbox',
  plan: 'Planning',
  analyze: 'Analyzing',
  implement: 'Implementing',
  implement_fix: 'Fixing',
  test: 'Testing',
  verify: 'Verifying',
  open_pr: 'Opening PR for',
  await_ci: 'Waiting for CI',
  ready_for_review: 'Ready for review',
  process_feedback: 'Processing feedback',
}

const stepDisplayNames: Record<SystemStepName, string> = {
  setup_repo: 'Sandbox setup',
  plan: 'Plan',
  analyze: 'Analyze',
  implement: 'Implement',
  implement_fix: 'Fix issues',
  test: 'Test',
  verify: 'Verify',
  open_pr: 'Open PR',
  await_ci: 'Wait for CI',
  ready_for_review: 'Ready for review',
  process_feedback: 'Process feedback',
}

const sandboxSetupReasonPatterns = [
  /^step:\s*(set up repository|setup repo|sandbox setup)$/i,
  /^setting up sandbox/i,
]

/**
 * Returns a human-readable display name for a step (e.g., "Setup repo").
 * Falls back to replacing underscores with spaces.
 */
export function getStepDisplayName(name: string): string {
  if (isSystemStepName(name)) {
    return stepDisplayNames[name]
  }
  return name.replace(/_/g, ' ')
}

/**
 * Returns the step verb for a given step name (e.g., "Implementing").
 * Falls back to the raw step name if unknown, or null if no step name.
 */
export function getStatusLabel(currentStepName: string | null) {
  if (!currentStepName) {
    return null
  }
  return isSystemStepName(currentStepName)
    ? stepLabels[currentStepName]
    : 'Working'
}

/**
 * Returns a full label combining the step verb with the title
 * (e.g., "Implementing My Brief").
 */
export function getImplementationLabel(currentStepName: string | null) {
  if (!currentStepName) {
    return null
  }
  return isSystemStepName(currentStepName)
    ? stepLabels[currentStepName]
    : currentStepName
}

export function normalizeStatusChangeReason(reason: string): string {
  return sandboxSetupReasonPatterns.some((pattern) => pattern.test(reason))
    ? 'Setting up sandbox...'
    : reason
}

/**
 * Pure helper that determines the sidebar display info for an implementation.
 * Returns the label text and an icon key ('spinner' | 'git-merge' | 'code').
 */
export function getImplementationDisplayInfo(impl: {
  status: string
  prStatus: string | null
  currentStepName: string | null
  title: string | null
  brief: { title: string } | null
  prompt: string | null
  isRunning?: boolean
  activity?: { key: string; label: string }
}) {
  const activityIsRunning = impl.activity
    ? isProgressingActivity(impl.activity.key)
    : false
  const isRunning = impl.activity
    ? activityIsRunning
    : (impl.isRunning ?? impl.status === 'running')
  const isCompletedWithOpenPR =
    impl.status === 'completed' && impl.prStatus === 'open'
  const title =
    impl.title ??
    impl.brief?.title ??
    impl.prompt?.split('\n')[0]?.slice(0, 50) ??
    'task'

  const label =
    impl.activity?.label ?? getImplementationLabel(impl.currentStepName)

  let icon: 'spinner' | 'git-merge' | 'code'
  if (isRunning) {
    icon = 'spinner'
  } else if (isCompletedWithOpenPR) {
    icon = 'git-merge'
  } else {
    icon = 'code'
  }

  return { title, label, icon }
}

/**
 * Formats the duration between two timestamps as a human-readable string.
 * - Completed/failed steps: completedAt - startedAt
 * - Running steps: now - startedAt (pass isRunning=true)
 * - Pending steps (null dates): returns "—"
 */
export function formatStepDuration(
  startedAt: Date | string | null,
  completedAt: Date | string | null,
  isRunning: boolean,
): string {
  if (!startedAt) return '—'

  const start = startedAt instanceof Date ? startedAt : new Date(startedAt)
  const end = isRunning
    ? new Date()
    : completedAt
      ? completedAt instanceof Date
        ? completedAt
        : new Date(completedAt)
      : null

  if (!end) return '—'

  // A stray/malformed value (e.g. `new Date('nonsense')`) yields an Invalid
  // Date whose `getTime()` is NaN; degrade to '—' rather than rendering 'NaNs'.
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—'

  const ms = end.getTime() - start.getTime()
  if (ms < 0) return '—'
  if (ms < 1000) return '< 1s'

  const totalSeconds = Math.floor(ms / 1000)
  const totalMinutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours > 0) return `${hours}h ${minutes}m`
  if (totalMinutes > 0) return `${totalMinutes}m ${seconds}s`
  return `${seconds}s`
}
